import React, { useRef, useEffect, useState } from "react";
import { connect } from "react-redux";
import BamboraSvgIcom from "../../../common/bamboraSvgIcon";
import { ROLE_MAPPING } from "../../constants";
import { impersonateUser } from "@frontrunners/wl-cognito-login";
import closeIcon from "../../../assets/close.png";
import { convertObjToBase64 } from "../../../common/locationHelper";
import { Badge, Button, ButtonV2, Modal, Secret } from "@frontrunners/ui-components";
import "./userListContainer.scss";
import { ssciamIsEnabled, loggedInUserHasScope } from "../../../common/authHelper";

const outsideRef = (ref, toggle) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        toggle(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);
};

const UserListSscIam = ({
  endpoints,
  users,
  impersonation: {
    merchantDetails: { id: merchantId },
  },
}) => {
  const [password, setPassword] = useState(null);
  const [confirmationBoxOpened, toggleConfirmationbox] = useState(false);
  const [error, setError] = useState(false);
  const [resetPasswordForUser, setResetPasswordForUser] = useState(null);
  const [resetPhoneForUser, setResetPhoneForUser] = useState(null);
  const targetEmail = useRef(null);
  const targetUserId = useRef(null);
  const [sscIamImpersonateEMail, setSscIamImpersonateEMail] = useState(null);
  const [fallback, setFallback] = useState(false);

  const impersonate = (email, userID) => {
    setFallback(false);
    setSscIamImpersonateEMail(email);
    targetEmail.current = email;
    targetUserId.current = userID;
  };

  useEffect(() => {
    if (fallback) {
      if (password) {
        const username = localStorage.getItem("cognitoUser");
        const poolData = {
          UserPoolId: endpoints.cognitoUserPoolId,
          ClientId: endpoints.cognitoMerchantPortalClientId,
        };
        fetchImpersonatedUser(poolData, username, password);
      } else {
        togglePasswordConfirmation();
      }
    }
  }, [fallback, password, endpoints.cognitoUserPoolId, endpoints.cognitoMerchantPortalClientId]);

  useEffect(() => {
    if (sscIamImpersonateEMail) {
      console.log("Impersonation email:", sscIamImpersonateEMail, merchantId);
      sscIamImpersonate(sscIamImpersonateEMail, merchantId);
    }
  }, [sscIamImpersonateEMail, merchantId, endpoints]);

  const sscIamImpersonate = async (email, merchantId) => {
    const { dxGateway, backofficeHost } = endpoints;
    const accessToken = window.localStorage.getItem("accessToken");
    try {
      const response = await fetch(
        `${dxGateway}/v1/merchants/${merchantId}/users/${sscIamImpersonateEMail}/impersonate/delegate/initiate`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json", // if you send/expect JSON
          },
        }
      );
      if (response.ok) {
        const code = await response.text();
        const tabId = Math.random().toString(36).slice(-4);
        window.open(
          `${backofficeHost}/ssciam/impersonate#code=${code}&userHint=${sscIamImpersonateEMail}&merchantHint=${merchantId}`,
          tabId
        );
      } else {
        setFallback(true);
      }
    } catch (err) {
      setFallback(true);
    } finally {
      setSscIamImpersonateEMail(null);
    }
  };

  const fetchImpersonatedUser = async (poolData, username, password) => {
    try {
      const response = await impersonateUser(poolData, username, password, targetEmail.current);
      const targetIdToken = response.idToken.jwtToken;
      const tabId = Math.random().toString(36).slice(-4);
      const state = convertObjToBase64({
        impersonateUserId: targetUserId.current,
      });
      const query = new URLSearchParams({
        access_token: targetIdToken,
        id_token: targetIdToken,
        state,
      }).toString();

      window.open(`${endpoints.backofficeHost}/?#${query}`, tabId);
      toggleConfirmationbox(false);
    } catch (error) {
      console.error(error);
      setError(true);
    }
  };

  const togglePasswordConfirmation = () => {
    setError(false);
    setPassword(null);
    toggleConfirmationbox(!confirmationBoxOpened);
  };

  const impersonateSelectedUser = async () => {
    const username = localStorage.getItem("cognitoUser");
    const poolData = {
      UserPoolId: endpoints.cognitoUserPoolId,
      ClientId: endpoints.cognitoMerchantPortalClientId,
    };
    fetchImpersonatedUser(poolData, username, password);
  };

  const onChange = (value) => {
    setPassword(value);
  };

  const ResetUserPhoneNumberModal = () => {
    return (
      <Modal
        onClose={() => {
          setResetPhoneForUser(null);
          setError(false);
        }}
      >
        <h1>
          This will reset the phone number for the <Badge className="warning">{resetPhoneForUser.email}</Badge> user
        </h1>
        <p>
          This will reset the curent phone number. At the next login the user will have to re-register with a new phone
          number.
        </p>
        <Button
          label="Yes, reset number"
          className="blank"
          onClick={() => {
            const accessToken = window.localStorage.getItem("accessToken");
            const url = `${endpoints.sscIamUserMgtApi}/admin/operations/users/${resetPhoneForUser.email}/credentials/otp/sms/reset`;
            const options = {
              method: "POST",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                reason: "User requested OTP reset",
              }),
            };
            fetch(url, options).then(async (response) => {
              const res = await response.json();
              if (response.status !== 200) {
                setError({ reason: res.error.message });
              } else {
                setError(false);
                setResetPhoneForUser(null);
              }
            });
          }}
        />
        <Button
          label="Cancel"
          className="default"
          onClick={() => {
            setError(false);
            setResetPhoneForUser(null);
          }}
        />
        {error && error.reason && <div className="error">{error.reason}</div>}
      </Modal>
    );
  };

  const ResetUserPasswordModal = () => {
    const [newPassword, setNewPassword] = useState(null);
    return (
      <Modal onClose={() => setResetPasswordForUser(null)}>
        <h1>
          This will reset the password for the <Badge className="warning">{resetPasswordForUser.email}</Badge>user
        </h1>
        <p>
          By performing this action, the user’s current password will immediately stop working. You will receive a
          one-time password that can only be used once, and it should be used by the user. Be cautious!
        </p>

        {newPassword && (
          <Secret
            secret={newPassword}
            showSecretLabelText="Show secret"
            copyButtonHoverText="Copy to clipboard"
            copyButtonClickText="Copied!"
          />
        )}

        {!newPassword && (
          <Button
            label="Yes, reset password"
            className="blank"
            onClick={() => {
              const accessToken = window.localStorage.getItem("accessToken");
              const url = `${endpoints.apiHost}/internal/v1/identity-management/users/${resetPasswordForUser.id}/set-password`;
              const options = {
                method: "POST",
                headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
              };
              fetch(url, options).then(async (response) => {
                const res = await response.json();
                if (response.status !== 200) {
                  setError(res);
                }
                setNewPassword(res.password);
              });
            }}
          />
        )}
        {!newPassword && (
          <Button
            label="Cancel"
            className="default"
            onClick={() => {
              setResetPasswordForUser(null);
            }}
          />
        )}
        {error && error.reason && <div className="error">{error.reason}</div>}
      </Modal>
    );
  };

  const canResetPhoneNumber = loggedInUserHasScope("idm_set_user_password");

  return users && users.length > 0 ? (
    <div className="user-table">
      <h2>Users</h2>
      {confirmationBoxOpened && (
        <PasswordConfirmationBox
          error={error}
          togglePasswordConfirmation={togglePasswordConfirmation}
          onChange={onChange}
          impersonateSelectedUser={impersonateSelectedUser}
        />
      )}
      {resetPasswordForUser && <ResetUserPasswordModal />}
      {resetPhoneForUser && <ResetUserPhoneNumberModal />}
      <table className="table" style={{ opacity: `${confirmationBoxOpened ? 0.4 : 1}` }}>
        <thead>
          <tr>
            {canResetPhoneNumber && <th width="15%" />}
            <th width="15%">Id</th>
            <th width="23%">Email</th>
            <th width="23%">Role</th>
            <th width="27%"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            return (
              <tr key={user.id}>
                {canResetPhoneNumber && (
                  <td>
                    <ButtonV2 label="Reset Phone" className="default" onClick={() => setResetPhoneForUser(user)} />
                  </td>
                )}
                <td>
                  <code className="short" title={user.id}>
                    {user.id}
                  </code>
                </td>
                <td>
                  <a href={"mailto:" + user.email}>{user.email}</a>
                </td>
                <td>{ROLE_MAPPING[user.merchant_roles[0].roles[0]]}</td>
                <td>
                  <button
                    disabled={sscIamImpersonateEMail}
                    className="btn"
                    onClick={() => impersonate(user.email, user.id)}
                  >
                    Impersonate
                    <BamboraSvgIcom icon="arrow-left" className="fill-inherit rotate-180 icon" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ) : (
    <p>This merchant has no users</p>
  );
};

const UserList = ({ endpoints, users }) => {
  const [password, setPassword] = React.useState(null);
  const [confirmationBoxOpened, toggleConfirmationbox] = useState(false);
  const [error, setError] = useState(false);
  const [resetPasswordForUser, setResetPasswordForUser] = useState(null);
  const [resetPhoneForUser, setResetPhoneForUser] = useState(null);
  const targetEmail = useRef(null);
  const targetUserId = useRef(null);

  const impersonate = (email, userID) => {
    targetEmail.current = email;
    targetUserId.current = userID;

    if (password) {
      const username = localStorage.getItem("cognitoUser");
      const poolData = { UserPoolId: endpoints.cognitoUserPoolId, ClientId: endpoints.cognitoMerchantPortalClientId };
      fetchImpersonatedUser(poolData, username, password);
    } else {
      togglePasswordConfirmation();
    }
  };

  const fetchImpersonatedUser = async (poolData, username, password) => {
    try {
      const response = await impersonateUser(poolData, username, password, targetEmail.current);
      const targetIdToken = response.idToken.jwtToken;
      const tabId = Math.random().toString(36).slice(-4);
      const state = convertObjToBase64({ impersonateUserId: targetUserId.current });
      const query = new URLSearchParams({
        access_token: targetIdToken,
        id_token: targetIdToken,
        state,
      }).toString();

      window.open(`${endpoints.backofficeHost}/?#${query}`, tabId);
      toggleConfirmationbox(false);
    } catch (error) {
      console.error(error);
      setError(true);
    }
  };

  const togglePasswordConfirmation = () => {
    setError(false);
    setPassword(null);
    toggleConfirmationbox(!confirmationBoxOpened);
  };

  const impersonateSelectedUser = async () => {
    const username = localStorage.getItem("cognitoUser");
    const poolData = { UserPoolId: endpoints.cognitoUserPoolId, ClientId: endpoints.cognitoMerchantPortalClientId };
    fetchImpersonatedUser(poolData, username, password);
  };

  const onChange = (value) => {
    setPassword(value);
  };

  const ResetUserPhoneNumberModal = () => {
    return (
      <Modal
        onClose={() => {
          setResetPhoneForUser(null);
          setError(false);
        }}
      >
        <h1>
          This will reset the phone number for the <Badge className="warning">{resetPhoneForUser.email}</Badge> user
        </h1>
        <p>
          This will reset the curent phone number. At the next login the user will have to re-register with a new phone
          number.
        </p>
        <Button
          label="Yes, reset number"
          className="blank"
          onClick={() => {
            const accessToken = window.localStorage.getItem("accessToken");
            const url = `${endpoints.sscIamUserMgtApi}/admin/operations/users/${resetPhoneForUser.email}/credentials/otp/sms/reset`;
            const options = {
              method: "POST",
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                reason: "User requested OTP reset",
              }),
            };
            fetch(url, options).then(async (response) => {
              const res = await response.json();
              if (response.status !== 200) {
                setError({ reason: res.error.message });
              } else {
                setError(false);
                setResetPhoneForUser(null);
              }
            });
          }}
        />
        <Button
          label="Cancel"
          className="default"
          onClick={() => {
            setError(false);
            setResetPhoneForUser(null);
          }}
        />
        {error && error.reason && <div className="error">{error.reason}</div>}
      </Modal>
    );
  };

  const ResetUserPasswordModal = () => {
    const [newPassword, setNewPassword] = useState(null);
    return (
      <Modal onClose={() => setResetPasswordForUser(null)}>
        <h1>
          This will reset the password for the <Badge className="warning">{resetPasswordForUser.email}</Badge>user
        </h1>
        <p>
          By performing this action, the user’s current password will immediately stop working. You will receive a
          one-time password that can only be used once, and it should be used by the user. Be cautious!
        </p>

        {newPassword && (
          <Secret
            secret={newPassword}
            showSecretLabelText="Show secret"
            copyButtonHoverText="Copy to clipboard"
            copyButtonClickText="Copied!"
          />
        )}

        {!newPassword && (
          <Button
            label="Yes, reset password"
            className="blank"
            onClick={() => {
              const accessToken = window.localStorage.getItem("accessToken");
              const url = `${endpoints.apiHost}/internal/v1/identity-management/users/${resetPasswordForUser.id}/set-password`;
              const options = {
                method: "POST",
                headers: {
                  Accept: "application/json",
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
              };
              fetch(url, options).then(async (response) => {
                const res = await response.json();
                if (response.status !== 200) {
                  setError(res);
                }
                setNewPassword(res.password);
              });
            }}
          />
        )}
        {!newPassword && (
          <Button
            label="Cancel"
            className="default"
            onClick={() => {
              setResetPasswordForUser(null);
            }}
          />
        )}
        {error && error.reason && <div className="error">{error.reason}</div>}
      </Modal>
    );
  };

  const canResetUserPassword = loggedInUserHasScope("idm_set_user_password");

  return users && users.length > 0 ? (
    <div className="user-table">
      <h2>Users</h2>
      {confirmationBoxOpened && (
        <PasswordConfirmationBox
          error={error}
          togglePasswordConfirmation={togglePasswordConfirmation}
          onChange={onChange}
          impersonateSelectedUser={impersonateSelectedUser}
        />
      )}
      {resetPasswordForUser && <ResetUserPasswordModal />}
      {resetPhoneForUser && <ResetUserPhoneNumberModal />}
      <table className="table" style={{ opacity: `${confirmationBoxOpened ? 0.4 : 1}` }}>
        <thead>
          <tr>
            {canResetUserPassword && <th width="15%" />}
            <th width="15%">Id</th>
            <th width="23%">Email</th>
            <th width="23%">Role</th>
            <th width="27%"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            return (
              <tr key={user.id}>
                {canResetUserPassword && (
                  <td>
                    <ButtonV2
                      label="Reset Password"
                      className="default"
                      onClick={() => setResetPasswordForUser(user)}
                    />
                  </td>
                )}
                <td>
                  <code className="short" title={user.id}>
                    {user.id}
                  </code>
                </td>
                <td>
                  <a href={"mailto:" + user.email}>{user.email}</a>
                </td>
                <td>{ROLE_MAPPING[user.merchant_roles[0].roles[0]]}</td>
                <td>
                  <button className="btn" onClick={() => impersonate(user.email, user.id)}>
                    Impersonate
                    <BamboraSvgIcom icon="arrow-left" className="fill-inherit rotate-180 icon" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  ) : (
    <p>This merchant has no users</p>
  );
};

const PasswordConfirmationBox = ({ onChange, togglePasswordConfirmation, impersonateSelectedUser, error }) => {
  const ref = useRef(null);
  outsideRef(ref, togglePasswordConfirmation);
  return (
    <div className="password-modal" ref={ref}>
      <div className="password-confirm">
        <div className="header">
          <h2>Confirm password</h2>
          <img src={closeIcon} alt="logo" className="close" onClick={() => togglePasswordConfirmation(false)} />
        </div>
        <p>Please verify your identity by entering your password again.</p>
        <div className="password-box">
          <input type="password" onChange={(e) => onChange(e.target.value)} placeholder="Password"></input>
        </div>
        {error && <span style={{ color: "red" }}>Something went wrong while trying to impersonate.</span>}
        <button className="btn" onClick={() => impersonateSelectedUser()}>
          Proceed
        </button>
      </div>
    </div>
  );
};

const UserListWrapperOld = (props) => {
  const enableSscIam = ssciamIsEnabled();
  return enableSscIam ? (
    <UserListSscIam endpoints={props.endpoints} users={props.users} impersonation={props.impersonation} />
  ) : (
    <UserList endpoints={props.endpoints} users={props.users} impersonation={props.impersonation} />
  );
};

const mapStateToProps = (state) => {
  return {
    endpoints: state.getIn(["config", "endpoints"]),
    users: state.getIn(["impersonation", "users"]),
    impersonation: state.get("impersonation").toJS(),
  };
};

export default connect(mapStateToProps)(UserListWrapperOld);
